<template>
  <div class="side-bar__panel side-bar__panel--backlinks">
    <div class="side-bar__info" v-if="!backlinks.length && !outgoingLinks.length">
      <p>No links found.</p>
      <p class="backlinks-hint">
        Link to other notes using <code>[[Note Name]]</code> syntax.
      </p>
    </div>

    <div v-if="outgoingLinks.length" class="backlinks-section">
      <div class="backlinks-section__title">Outgoing Links ({{ outgoingLinks.length }})</div>
      <div
        v-for="link in outgoingLinks"
        :key="link.id"
        class="backlinks-item"
        @click="openFile(link)"
      >
        <div class="backlinks-item__name">{{ link.name }}</div>
      </div>
    </div>

    <div v-if="backlinks.length" class="backlinks-section">
      <div class="backlinks-section__title">Backlinks ({{ backlinks.length }})</div>
      <div
        v-for="link in backlinks"
        :key="link.id"
        class="backlinks-item"
        @click="openFile(link)"
      >
        <div class="backlinks-item__name">{{ link.name }}</div>
        <div class="backlinks-item__preview" v-if="link.preview">{{ link.preview }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import store from '../store';
import localDbSvc from '../services/localDbSvc';

export default {
  name: 'BacklinksPanel',
  computed: {
    ...mapState('file', ['itemsById']),
    currentFile() {
      return store.getters['file/current'];
    },
    currentContent() {
      return store.getters['content/current'];
    },
    files() {
      return store.getters['file/items'].filter(
        file => file.type === 'file' && file.parentId !== 'trash'
      );
    },
    outgoingLinks() {
      const content = this.currentContent?.text || '';
      const linkPattern = /\[\[([^\]]+)\]\]/g;
      const links = [];
      let match;

      while ((match = linkPattern.exec(content)) !== null) {
        const linkName = match[1].trim();
        const linkedFile = this.files.find(
          f => f.name.toLowerCase() === linkName.toLowerCase()
        );
        if (linkedFile && !links.find(l => l.id === linkedFile.id)) {
          links.push({
            id: linkedFile.id,
            name: linkedFile.name,
          });
        }
      }
      return links;
    },
    backlinks() {
      const currentName = this.currentFile?.name;
      if (!currentName) return [];

      const links = [];
      const pattern = new RegExp(`\\[\\[${this.escapeRegex(currentName)}\\]\\]`, 'i');

      this.files.forEach((file) => {
        if (file.id === this.currentFile.id) return;

        const contentId = `${file.id}/content`;
        const content = store.state.content.itemsById[contentId];

        if (content?.text && pattern.test(content.text)) {
          const preview = this.getPreview(content.text, currentName);
          links.push({
            id: file.id,
            name: file.name,
            preview,
          });
        }
      });

      return links;
    },
  },
  methods: {
    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    getPreview(text, linkName) {
      const pattern = new RegExp(`\\[\\[${this.escapeRegex(linkName)}\\]\\]`, 'i');
      const match = text.match(pattern);
      if (!match) return '';

      const index = match.index;
      const start = Math.max(0, index - 30);
      const end = Math.min(text.length, index + match[0].length + 30);
      let preview = text.slice(start, end).replace(/\n/g, ' ');

      if (start > 0) preview = '...' + preview;
      if (end < text.length) preview = preview + '...';

      return preview;
    },
    async openFile(link) {
      const file = this.itemsById[link.id];
      if (file) {
        await localDbSvc.loadItem(`${file.id}/content`);
        store.commit('file/setCurrentId', file.id);
        store.dispatch('data/toggleSideBar', false);
      }
    },
  },
};
</script>

<style lang="scss">
.side-bar__panel--backlinks {
  padding: 10px;

  .side-bar__info {
    padding: 10px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 14px;

    .app--dark &,
    .app--nord &,
    .app--solarized-dark &,
    .app--dracula &,
    .app--monokai & {
      color: rgba(255, 255, 255, 0.5);
    }

    .app--sepia & {
      color: rgba(91, 70, 54, 0.6);
    }

    .app--solarized-light & {
      color: rgba(88, 110, 117, 0.6);
    }

    p {
      margin: 0 0 8px;
    }
  }

  .backlinks-hint {
    font-size: 12px;

    code {
      background: rgba(0, 0, 0, 0.08);
      padding: 2px 4px;
      border-radius: 3px;

      .app--dark &,
      .app--nord &,
      .app--solarized-dark &,
      .app--dracula &,
      .app--monokai & {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }
}

.backlinks-section {
  margin-bottom: 16px;
}

.backlinks-section__title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.5);
  padding: 8px 10px;

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.5);
  }

  .app--sepia & {
    color: rgba(91, 70, 54, 0.6);
  }

  .app--solarized-light & {
    color: rgba(88, 110, 117, 0.6);
  }
}

.backlinks-item {
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 4px;
  color: rgba(0, 0, 0, 0.87);

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.87);
  }

  .app--sepia & {
    color: #5b4636;
  }

  .app--solarized-light & {
    color: #586e75;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);

    .app--dark &,
    .app--nord &,
    .app--solarized-dark &,
    .app--dracula &,
    .app--monokai & {
      background: rgba(255, 255, 255, 0.05);
    }

    .app--sepia & {
      background: rgba(91, 70, 54, 0.05);
    }

    .app--solarized-light & {
      background: rgba(88, 110, 117, 0.05);
    }
  }
}

.backlinks-item__name {
  font-size: 14px;
  font-weight: 500;
}

.backlinks-item__preview {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.5);
  }

  .app--sepia & {
    color: rgba(91, 70, 54, 0.6);
  }

  .app--solarized-light & {
    color: rgba(88, 110, 117, 0.6);
  }
}
</style>
